// Product.js
import React, { useState, useContext, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import useCartStore from "./store/shoppingCardStore";
import styles from "./styles/productPage.module.css";
import backarrow from "./images/back arrow.png";
import { FiPlus, FiMinus, FiShoppingCart } from "react-icons/fi";
import { Helmet } from "react-helmet";
import { ProductsContext } from "./context/ProductContextProvider";
import { slugify } from "./utils/utils";
// Import the new component
import CallUsFloatingButton from "./components/CallUsFloatingButton";

const Product = () => {
  const { productSlug } = useParams();
  const { products } = useContext(ProductsContext);
  const navigate = useNavigate();

  const add = useCartStore((state) => state.addItem);
  const remove = useCartStore((state) => state.removeItem);
  const { itemscount } = useCartStore();

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [prevProduct, setPrevProduct] = useState(null);
  const [nextProduct, setNextProduct] = useState(null);

  useEffect(() => {
    if (products && products.length > 0) {
      const foundIndex = products.findIndex(
        (p) => slugify(p.name) === productSlug
      );

      if (foundIndex !== -1) {
        setProduct(products[foundIndex]);
        setActiveImage(0); // Reset active image when product changes
        setPrevProduct(foundIndex > 0 ? products[foundIndex - 1] : null);
        setNextProduct(
          foundIndex < products.length - 1 ? products[foundIndex + 1] : null
        );
      } else {
        navigate("/محصولات"); // Changed to /محصولات for consistency with original file
      }
    } else if (products === null) {
      // Products context might still be loading or there's an issue
      // You might want to display a more specific loading/error message here
    }
  }, [productSlug, products, navigate]);

  if (!product) {
    return (
      <>
        <Navbar />
        <div className={styles.loadingState}>در حال بارگذاری محصول...</div>
        <Footer />
      </>
    );
  }

  const isOutOfStock = product.name.includes("(ناموجود)");
  const isUPSBattery = product.name.includes("UPS"); // Check if 'UPS' is in the product name

  return (
    <div className={styles.productPage}>
      <Helmet>
        {/* متاتگ‌های اصلی */}
        <title>{product.meta?.title || product.name} | باتری۱۰۰</title>{" "}
        {/* Added | باتری۱۰۰ */}
        <meta
          name="description"
          content={product.meta?.description || product.info}
        />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.info} />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content={`https://battery100.ir/product/${productSlug}`}
        />
        {/* متاتگ‌های تصویر (Modified to avoid fragment) */}
        {product.image && product.image[0] && (
          <meta property="og:image" content={product.image[0]} />
        )}
        {product.image && product.image[0] && (
          <meta name="product_image" content={product.image[0]} />
        )}
        {/* متاتگ‌های قیمت */}
        <meta property="product:price:amount" content={product.price || ""} />
        <meta property="product:price:currency" content="IRR" />
        <meta name="product_price" content={product.price || ""} />
        <meta
          name="product_old_price"
          content={product.oldPrice || product.price || ""}
        />
        {/* متاتگ‌های اختصاصی ترب */}
        <meta name="product_id" content={product.id || ""} />
        <meta name="product_name" content={product.name || ""} />
        <meta
          name="availability"
          content={isOutOfStock ? "outofstock" : "instock"}
        />
        <meta
          name="guarantee"
          content={product.guarantee || "12 ماه گارانتی"}
        />
        {/* Schema Markup for Product (Re-added from previous version as it's important for SEO) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: product.name,
            image: product.image,
            description: product.info,
            sku: product.id,
            offers: {
              "@type": "Offer",
              url: `https://battery100.ir/product/${productSlug}`,
              priceCurrency: "IRR",
              price: product.price,
              itemCondition: "https://schema.org/NewCondition",
              availability: isOutOfStock
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>
      <Navbar />

      <main className={styles.contentWrapper}>
        {" "}
        {/* Adjusted to contentWrapper for consistency with CSS */}
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb" className={styles.breadcrumb}>
          <ol>
            <li>
              <Link to="/">خانه</Link>
            </li>
            <li>
              <Link to="/محصولات/">محصولات</Link>
            </li>
            <li aria-current="page">{product.name}</li>
          </ol>
        </nav>
        {/* Back Button */}
        <Link to="/محصولات/" className={styles.backButton}>
          {" "}
          {/* Adjusted to Link and /محصولات */}
          <img
            src={backarrow}
            alt="بازگشت به صفحه محصولات"
            title="بازگشت به لیست محصولات"
            className={styles.backarrow}
            width="20"
            height="20"
          />
          <span>بازگشت به محصولات</span>
        </Link>
        <div className={styles.productContainer}>
          {/* Product Gallery Section */}
          <div className={styles.productGallery}>
            <figure>
              {product.image && (
                <img
                  src={product.image[activeImage]}
                  alt={`${product.name} - تصویر اصلی`}
                  title={`مشخصات ${product.name}`}
                  className={styles.mainImage}
                  loading="lazy"
                  width="500"
                  height="500"
                />
              )}
            </figure>

            {product.image && product.image.length > 1 && (
              <div className={styles.thumbnailContainer} role="tablist">
                {product.image.map((img, index) => (
                  <button
                    key={index}
                    className={`${styles.thumbnailWrapper} ${
                      index === activeImage ? styles.active : ""
                    }`}
                    onClick={() => setActiveImage(index)}
                    aria-label={`نمایش تصویر ${index + 1} از ${product.name}`}
                    role="tab"
                    aria-selected={index === activeImage}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - تصویر ${index + 1}`}
                      className={styles.thumbnail}
                      loading="lazy"
                      width="100"
                      height="100"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details Section */}
          <article className={styles.productDetails}>
            <header>
              <h1 className={styles.productTitle}>{product.name}</h1>
              <p className={styles.productCategory}>
                دسته‌بندی: {isUPSBattery ? "باتری یو پی اس" : "باتری ماشین"}
              </p>{" "}
              <div className={styles.priceSection}>
                <span className={styles.priceLabel}>قیمت:</span>{" "}
                {/* Added priceLabel for consistency with original CSS */}
                <span className={styles.priceValue}>
                  {product.price.toLocaleString()} تومان
                </span>
              </div>
            </header>

            <section
              className={styles.productDescription}
              aria-labelledby="product-desc-heading"
            >
              {/* <h2 id="product-desc-heading" className="visually-hidden">
                مشخصات محصول
              </h2> */}
              <div dangerouslySetInnerHTML={{ __html: product.info }} />
            </section>

            <section
              className={styles.productFeatures}
              aria-labelledby="product-specs-heading"
            >
              <h2 id="product-specs-heading">ویژگی‌های فنی {product.name}</h2>{" "}
              {/* Adjusted for consistency with original file */}
              <div
                className={styles.productSpecs} // Added this class as per original file
                dangerouslySetInnerHTML={{
                  __html:
                    product.infos ||
                    `
                    <ul>
                      <li><strong>نوع:</strong> اتمی سیلد (خشک)</li>
                      <li><strong>آمپر ساعت:</strong> 60Ah</li>
                      <li><strong>ولتاژ:</strong> 12 ولت</li>
                      <li><strong>ابعاد:</strong> 227×175×242 میلی‌متر</li>
                      <li><strong>وزن:</strong> 16.9 کیلوگرم</li>
                      <li><strong>گارانتی:</strong> 12 ماه</li>
                      <li><strong>سازنده:</strong> شرکت صبا باتری</li>
                    </ul>
                  `,
                }}
              />
            </section>

            <div className={styles.actions}>
              {" "}
              {/* Changed to actions for consistency with original file */}
              {isOutOfStock ? (
                <button className={styles.outOfStockButton} disabled>
                  ناموجود
                </button>
              ) : itemscount[product.id] > 0 ? (
                <>
                  <div className={styles.quantityControl}>
                    <button
                      onClick={() => remove(product.id)}
                      className={styles.quantityButton}
                      aria-label="کاهش تعداد"
                    >
                      <FiMinus />
                    </button>
                    <span className={styles.quantityValue}>
                      {itemscount[product.id]}
                    </span>
                    <button
                      onClick={() => add(product)}
                      className={styles.quantityButton}
                      aria-label="افزایش تعداد"
                    >
                      <FiPlus />
                    </button>
                  </div>

                  <div className={styles.totalPrice}>
                    <span>مبلغ قابل پرداخت:</span>
                    <span className={styles.totalValue}>
                      {(
                        product.price * itemscount[product.id]
                      ).toLocaleString()}{" "}
                      تومان
                    </span>
                  </div>

                  <Link to="/سبد خرید/" className={styles.checkoutButton}>
                    <FiShoppingCart />
                    <span>تکمیل خرید</span>
                  </Link>
                </>
              ) : (
                <button
                  onClick={() => add(product)}
                  className={styles.addToCartButton}
                  aria-label={`افزودن ${product.name} به سبد خرید`}
                >
                  <FiShoppingCart />
                  <span>افزودن به سبد خرید</span>
                </button>
              )}
            </div>
          </article>
        </div>
        {/* --- Product Suggestions (Previous/Next as related items) --- */}
        <section className={styles.suggestionSection}>
          <h2>محصولات پیشنهادی</h2>{" "}
          {/* Adjusted to products پیشنهادی for consistency with original file */}
          <div className={styles.suggestionCardsContainer}>
            {prevProduct && (
              <Link
                to={`/product/${slugify(prevProduct.name)}`}
                className={styles.suggestionCard}
              >
                <img
                  src={prevProduct.image[0]}
                  alt={prevProduct.name}
                  className={styles.suggestionImage}
                  loading="lazy"
                />
                <div className={styles.suggestionText}>
                  <h3>{prevProduct.name}</h3>
                  <span className={styles.suggestionPrice}>
                    {prevProduct.price.toLocaleString()} تومان
                  </span>
                </div>
              </Link>
            )}

            {nextProduct && (
              <Link
                to={`/product/${slugify(nextProduct.name)}`}
                className={styles.suggestionCard}
              >
                <img
                  src={nextProduct.image[0]}
                  alt={nextProduct.name}
                  className={styles.suggestionImage}
                  loading="lazy"
                />
                <div className={styles.suggestionText}>
                  <h3>{nextProduct.name}</h3>
                  <span className={styles.suggestionPrice}>
                    {nextProduct.price.toLocaleString()} تومان
                  </span>
                </div>
              </Link>
            )}

            {!prevProduct && !nextProduct && (
              <p className={styles.noSuggestions}>
                محصول دیگری برای پیشنهاد وجود ندارد.
              </p>
            )}
          </div>
        </section>
      </main>

      <Footer />
      {/* Add the ContactFloatingButton here */}
      <CallUsFloatingButton />
    </div>
  );
};

export default Product;
